import * as React from 'react';
import { useEffect, useMemo, useState } from 'react';
import Alert from '@mui/joy/Alert';
import AspectRatio from '@mui/joy/AspectRatio';
import Box from '@mui/joy/Box';
import Button from '@mui/joy/Button';
import Card from '@mui/joy/Card';
import CardContent from '@mui/joy/CardContent';
import CardOverflow from '@mui/joy/CardOverflow';
import Chip from '@mui/joy/Chip';
import CircularProgress from '@mui/joy/CircularProgress';
import Grid from '@mui/joy/Grid';
import Stack from '@mui/joy/Stack';
import Typography from '@mui/joy/Typography';
import DownloadRoundedIcon from '@mui/icons-material/DownloadRounded';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import LocalPostOfficeOutlinedIcon from '@mui/icons-material/LocalPostOfficeOutlined';
import { toast, ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';

import GeneralApiService from '../../../axios/Axios';
import freeExteriorCleanLeaflet from '../../../images/leaflets/free-exterior-clean-roof-clean.png';

interface FranchiseContext {
    id?: number;
    location?: string;
    twilio_phone?: string | null;
}

interface PhoneBox {
    // Coordinates relative to the source image (in source-pixel units).
    x: number;
    y: number;
    width: number;
    height: number;
}

interface LeafletTemplate {
    id: string;
    name: string;
    description: string;
    image: string;
    sourceWidth: number;
    sourceHeight: number;
    phoneBox: PhoneBox;
    fileNamePrefix: string;
}

// Coordinates calibrated against the 724x1024 source image. The empty quote box
// interior runs from x=94..629 and y=706..765 (border excluded).
const LEAFLETS: LeafletTemplate[] = [
    {
        id: 'free-exterior-clean-roof-clean',
        name: 'Free Exterior Clean with Roof Clean',
        description:
            'A4 flyer promoting the free full exterior clean offer when a customer books a roof clean. The instant-quote box is automatically filled with the current franchise number.',
        image: freeExteriorCleanLeaflet,
        sourceWidth: 724,
        sourceHeight: 1024,
        phoneBox: {
            x: 94,
            y: 706,
            width: 536,
            height: 60,
        },
        fileNamePrefix: 'YetiClean-Roof-Clean-Leaflet',
    },
];

function formatUkPhoneForDisplay(raw: string | null | undefined): string | null {
    if (!raw) return null;
    const digits = raw.replace(/\D+/g, '');
    if (!digits) return null;

    let national: string | null = null;
    if (digits.startsWith('44') && digits.length >= 11) {
        national = '0' + digits.slice(2);
    } else if (digits.startsWith('0') && digits.length >= 10) {
        national = digits;
    } else if (digits.length === 10) {
        national = '0' + digits;
    }

    if (!national) {
        return raw.trim();
    }

    if (national.startsWith('07') && national.length === 11) {
        return `${national.slice(0, 5)} ${national.slice(5)}`;
    }

    if (national.length === 11) {
        return `${national.slice(0, 4)} ${national.slice(4, 7)} ${national.slice(7)}`;
    }
    if (national.length === 10) {
        return `${national.slice(0, 4)} ${national.slice(4, 7)} ${national.slice(7)}`;
    }
    return national;
}

function loadImage(src: string): Promise<HTMLImageElement> {
    return new Promise((resolve, reject) => {
        const img = new Image();
        img.onload = () => resolve(img);
        img.onerror = (err) => reject(err);
        img.src = src;
    });
}

async function generateLeafletPng(template: LeafletTemplate, displayPhone: string): Promise<Blob> {
    const img = await loadImage(template.image);

    const canvas = document.createElement('canvas');
    canvas.width = template.sourceWidth;
    canvas.height = template.sourceHeight;
    const ctx = canvas.getContext('2d');
    if (!ctx) throw new Error('Canvas context unavailable');

    ctx.drawImage(img, 0, 0, template.sourceWidth, template.sourceHeight);

    const { x, y, width, height } = template.phoneBox;

    // Aim to fill the box while leaving small visual padding.
    const targetWidth = width * 0.92;
    const targetHeight = height * 0.78;

    ctx.fillStyle = '#000000';
    ctx.textAlign = 'center';
    // Use 'alphabetic' so we can place the baseline ourselves using the font's
    // true ascent/descent metrics. 'middle' is implemented inconsistently across
    // browsers/fonts and tends to drift up or down by several pixels.
    ctx.textBaseline = 'alphabetic';

    const fontFamily = 'Arial, Helvetica, sans-serif';
    // Start large enough that the height constraint usually wins, then shrink
    // until the text fits the width too.
    let fontSize = Math.floor(targetHeight * 1.25);
    ctx.font = `bold ${fontSize}px ${fontFamily}`;
    while (ctx.measureText(displayPhone).width > targetWidth && fontSize > 12) {
        fontSize -= 1;
        ctx.font = `bold ${fontSize}px ${fontFamily}`;
    }

    const metrics = ctx.measureText(displayPhone);
    const ascent =
        (metrics as any).actualBoundingBoxAscent ?? fontSize * 0.75;
    const descent =
        (metrics as any).actualBoundingBoxDescent ?? fontSize * 0.2;
    const glyphHeight = ascent + descent;

    const centerX = x + width / 2;
    const centerY = y + height / 2;
    const baselineY = centerY + glyphHeight / 2 - descent;

    ctx.fillText(displayPhone, centerX, baselineY);

    return await new Promise<Blob>((resolve, reject) => {
        canvas.toBlob((blob) => {
            if (blob) resolve(blob);
            else reject(new Error('Failed to encode PNG'));
        }, 'image/png');
    });
}

function triggerBlobDownload(blob: Blob, filename: string) {
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.setAttribute('download', filename);
    document.body.appendChild(link);
    link.click();
    link.remove();
    window.URL.revokeObjectURL(url);
}

function sanitiseForFilename(value: string): string {
    return value.replace(/[^a-z0-9-]+/gi, '-').replace(/^-+|-+$/g, '').toLowerCase();
}

export default function LeafletDownloads() {
    const selectedLocation = useMemo(() => localStorage.getItem('location') || '', []);
    const isAll = selectedLocation.toLowerCase() === 'all';

    const [franchise, setFranchise] = useState<FranchiseContext | null>(null);
    const [loading, setLoading] = useState<boolean>(!isAll);
    const [downloading, setDownloading] = useState<Record<string, boolean>>({});

    useEffect(() => {
        let cancelled = false;
        if (isAll || !selectedLocation) {
            setFranchise(null);
            setLoading(false);
            return;
        }
        setLoading(true);
        GeneralApiService.getFranchiseByName()
            .then((data: FranchiseContext) => {
                if (!cancelled) setFranchise(data);
            })
            .catch(() => {
                if (!cancelled) {
                    setFranchise(null);
                    toast.error('Failed to load franchise details for leaflet downloads.');
                }
            })
            .finally(() => {
                if (!cancelled) setLoading(false);
            });
        return () => {
            cancelled = true;
        };
    }, [isAll, selectedLocation]);

    const displayPhone = useMemo(
        () => formatUkPhoneForDisplay(franchise?.twilio_phone),
        [franchise?.twilio_phone]
    );

    const blocked = isAll || !franchise || !displayPhone;

    let blockedReason: string | null = null;
    if (isAll) {
        blockedReason =
            'Please select a specific franchise location (not "All") to download leaflets pre-filled with that franchise\'s phone number.';
    } else if (!loading && !franchise) {
        blockedReason = 'Could not load the selected franchise. Please refresh and try again.';
    } else if (!loading && franchise && !displayPhone) {
        blockedReason = `The selected franchise (${franchise.location || selectedLocation}) has no Twilio phone configured.`;
    }

    const handleDownload = async (template: LeafletTemplate) => {
        if (blocked || !displayPhone || !franchise) return;
        setDownloading((prev) => ({ ...prev, [template.id]: true }));
        try {
            const blob = await generateLeafletPng(template, displayPhone);
            const locationSlug = sanitiseForFilename(franchise.location || selectedLocation || 'franchise');
            const filename = `${template.fileNamePrefix}-${locationSlug}.png`;
            triggerBlobDownload(blob, filename);
            toast.success('Leaflet PNG downloaded');
        } catch (err) {
            console.error('Failed to generate leaflet PNG', err);
            toast.error('Failed to generate the leaflet image. Please try again.');
        } finally {
            setDownloading((prev) => ({ ...prev, [template.id]: false }));
        }
    };

    return (
        <>
            <ToastContainer position="top-right" autoClose={4000} theme="dark" />

            <Stack direction={{ xs: 'column', md: 'row' }} spacing={1.5} alignItems={{ md: 'center' }} sx={{ mb: 2 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Chip
                        size="sm"
                        variant="soft"
                        color={blocked ? 'warning' : 'success'}
                        startDecorator={<LocalPostOfficeOutlinedIcon fontSize="small" />}
                    >
                        {loading
                            ? 'Loading franchise…'
                            : isAll
                                ? 'All locations'
                                : franchise?.location || selectedLocation || 'No franchise selected'}
                    </Chip>
                    {!loading && displayPhone && (
                        <Chip size="sm" variant="outlined" color="neutral">
                            Phone: {displayPhone}
                        </Chip>
                    )}
                </Box>
            </Stack>

            {blockedReason && (
                <Alert color="warning" variant="soft" startDecorator={<InfoOutlinedIcon />} sx={{ mb: 2 }}>
                    {blockedReason}
                </Alert>
            )}

            <Grid container spacing={2}>
                {LEAFLETS.map((template) => {
                    const isDownloading = !!downloading[template.id];
                    return (
                        <Grid xs={12} sm={6} md={4} lg={3} key={template.id}>
                            <Card variant="outlined" sx={{ height: '100%' }}>
                                <CardOverflow>
                                    <Box sx={{ width: '45%', mx: 'auto', pt: 1.5 }}>
                                        <AspectRatio
                                            ratio={`${template.sourceWidth}/${template.sourceHeight}`}
                                            sx={{ bgcolor: 'background.level1', borderRadius: 'sm' }}
                                        >
                                            <img
                                                src={template.image}
                                                alt={template.name}
                                                loading="lazy"
                                                style={{ objectFit: 'contain' }}
                                            />
                                        </AspectRatio>
                                    </Box>
                                </CardOverflow>
                                <CardContent sx={{ gap: 1 }}>
                                    <Typography level="title-md">{template.name}</Typography>
                                    <Typography level="body-sm" sx={{ minHeight: 60 }}>
                                        {template.description}
                                    </Typography>
                                    <Button
                                        startDecorator={isDownloading ? <CircularProgress size="sm" /> : <DownloadRoundedIcon />}
                                        disabled={blocked || isDownloading}
                                        onClick={() => handleDownload(template)}
                                    >
                                        {isDownloading ? 'Generating…' : 'Download PNG'}
                                    </Button>
                                </CardContent>
                            </Card>
                        </Grid>
                    );
                })}
            </Grid>
        </>
    );
}
